import { openReportIssue } from "../store/reportIssueStore";

export type AppToastType = "error" | "notice" | "complete";

/**
 * 전역 toast의 화면별 배치. 생략하면 화면 하단 32px 중앙이다.
 * `editorViewport`는 bottom toolbar가 있는 Editor 화면의 위치 정책
 * (toolbar 위 16px · Unity viewport 중앙)을 적용한다.
 */
export type AppToastPlacement = "editorViewport";

export interface AppToastPayload {
  message: string;
  type: AppToastType;
  duration: number | null;
  actionLabel?: string;
  onAction?: () => void;
  onDismiss?: () => void;
  editorProject?: string | null;
  placement?: AppToastPlacement;
  /**
   * 이 toast가 대표하는 작업의 식별자. 대상이 사라졌을 때
   * `dismissAppToast(key)`로 그 toast만 닫기 위해 쓴다.
   */
  key?: string;
}

export interface AppToastAction {
  label: string;
  onAction: () => void;
}

type AppToastListener = (payload: AppToastPayload | null) => void;

let listener: AppToastListener | null = null;
let activePayload: AppToastPayload | null = null;
let activeEditorProject: string | null | undefined;

/** Called by AppToastProvider on mount/unmount. */
export function subscribeAppToast(next: AppToastListener | null): void {
  listener = next;
}

/**
 * Show app-wide toast (matches `Toast` / Figma styling).
 * Safe to call from hooks and non-React modules; falls back to console if provider not mounted.
 */
export function appToast(
  message: string,
  type: AppToastType = "notice",
  duration: number | null = 3000,
  action?: AppToastAction,
  onDismiss?: () => void,
  editorProject?: string | null,
  placement?: AppToastPlacement,
  key?: string,
): void {
  const payload: AppToastPayload = {
    message,
    type,
    duration,
    actionLabel: action?.label,
    onAction: action?.onAction,
    onDismiss,
    editorProject,
    placement,
    key,
  };
  if (
    editorProject !== undefined &&
    (!editorProject || editorProject !== activeEditorProject)
  ) {
    onDismiss?.();
    return;
  }
  if (listener) {
    activePayload = payload;
    listener(payload);
    return;
  }
  const prefix =
    type === "error" ? "[toast error]" : type === "complete" ? "[toast]" : "[toast notice]";
  console.warn(prefix, message);
}

/**
 * 같은 `key`로 띄운 toast가 아직 떠 있으면 닫는다. 다른 toast가 이미
 * 그 자리를 차지했으면 아무것도 하지 않는다.
 */
export function dismissAppToast(key: string): void {
  if (!key || activePayload?.key !== key) return;
  activePayload = null;
  listener?.(null);
}

/** Navigation changes presentation only; cancellation remains with each operation owner. */
export function dismissTransientAppToast(): void {
  if (!activePayload || activePayload.type === "error") return;
  activePayload = null;
  listener?.(null);
}

/** 현재 Editor project scope. 호출 시점에 scope가 없으면 undefined(무범위 toast)다. */
export function getAppToastEditorProjectScope(): string | null | undefined {
  return activeEditorProject;
}

export function setAppToastEditorProjectScope(
  editorProject: string | null | undefined,
): void {
  activeEditorProject = editorProject;
  if (
    activePayload?.type === "error" ||
    activePayload?.editorProject === undefined ||
    (activePayload.editorProject &&
      activePayload.editorProject === editorProject)
  ) {
    return;
  }
  activePayload = null;
  listener?.(null);
}

/** 에러 토스트에서 리포트로 넘어가는 액션. 문구는 Figma 그대로 Report issue 다. */
export const REPORT_ISSUE_TOAST_LABEL = "Report issue";

/**
 * 증상 목록에 대응하는 에러에만 붙인다. 모든 에러에 무차별로 붙이면 사용자가
 * 다음 화면에서 무엇을 고를지 예측할 수 없다.
 */
export const reportIssueToastAction = (): AppToastAction => ({
  label: REPORT_ISSUE_TOAST_LABEL,
  onAction: () => openReportIssue("toast"),
});
